
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { toast } from "sonner";
import { WritingControls } from "./WritingControls";
import { EvaluationResults } from "./EvaluationResults";
import { useEvaluation } from "./hooks/useEvaluation";
import { useAutoSave } from "./hooks/useAutoSave";
import { useWordCount } from "@/components/practice/hooks/useWordCount";
import { SaveDraftButton } from "@/components/practice/SaveDraftButton";
import { WritingEditor } from "@/components/practice/WritingEditor";
import { SubmitButton } from "@/components/practice/SubmitButton";
import { useProfile } from "@/hooks/use-profile";
import { draftService } from "@/services/draftService";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Evaluation } from "@/types/evaluation";
import { Button } from "@/components/ui/button";
import { Share2 } from "lucide-react";
import { ShareDialog } from "./ShareDialog";
import { WritingAreaSkeleton } from "./WritingAreaSkeleton";

interface Topic {
  id: string;
  topic: string;
  question: string;
  image_url?: string | null;
  task_type: number;
}

interface WritingAreaProps {
  topicId: string;
  taskType: number;
  isChangingTopic: boolean;
  onStartOver?: () => void;
  topic?: Topic | null;
}

export const WritingArea = ({
  topicId,
  taskType,
  isChangingTopic,
  onStartOver,
  topic
}: WritingAreaProps) => {
  const [searchParams] = useSearchParams();
  const draftIdFromUrl = searchParams.get("draft");

  const [content, setContent] = useState("");
  const [showWordCount, setShowWordCount] = useState(true);
  const [showTimer, setShowTimer] = useState(true);
  const [draftId, setDraftId] = useState<string | null>(draftIdFromUrl);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(!!draftIdFromUrl);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState("");

  const wordCount = useWordCount(content);
  const { user } = useAuth();
  const { profile } = useProfile();
  const { data: evaluation, refetch: refetchEvaluation } = useEvaluation(draftId);

  useEffect(() => {
    const loadDraft = async () => {
      if (draftId) {
        try {
          setIsLoading(true);
          const draft = await draftService.load(draftId);
          setContent(draft.content);
        } catch (error) {
          console.error("Error loading draft:", error);
          toast.error("Failed to load draft");
        } finally {
          setIsLoading(false);
        }
      }
    };

    loadDraft();
  }, [draftId]);

  useAutoSave(content, wordCount, draftId, user?.id, topicId, setDraftId);

  const handleSubscribe = async () => {
    try {
      if (content.trim() && !draftId && user?.id) {
        const { data, error } = await supabase
          .from("drafts")
          .insert({
            user_id: user.id,
            topic_id: topicId,
            content,
            word_count: wordCount,
          })
          .select()
          .single();

        if (error) throw error;
        setDraftId(data.id);
      }

      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: {
          returnUrl: draftId
            ? `/practice?draft=${draftId}`
            : '/dashboard'
        }
      });

      if (error) throw error;

      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Error in subscription process:', error);
      toast.error('Failed to start checkout process');
    }
  };

  const handleSubmit = async () => {
    if (!topicId) {
      toast.error("Please select a topic to evaluate this draft.");
      highlightTopicSelector();
      return;
    }

    if (!profile?.has_paid) {
      toast.error("Please subscribe to submit your essay for evaluation");
      return;
    }

    if (profile.remaining_attempts <= 0) {
      toast.error("You have no remaining attempts");
      return;
    }

    setIsSubmitting(true);
    const loadingToast = toast.loading("Evaluating your essay...");

    try {
      const { data: topicData } = await supabase
        .from('topics')
        .select('topic, question, internal_image_description')
        .eq('id', topicId)
        .single();

      if (!topicData) {
        throw new Error("Failed to load topic details");
      }

      const newDraftId = await draftService.save({
        draftId,
        content,
        wordCount,
        userId: user?.id!,
        topicId
      });
      setDraftId(newDraftId);

      const { data: evaluationData, error: evaluationError } = await supabase.functions.invoke<Evaluation>(
        'evaluate-essay',
        {
          body: {
            draftId: newDraftId,
            taskType,
            topicTitle: topicData.topic,
            topicDescription: topicData.question,
            topicImageDescription: topicData.internal_image_description,
            content,
            wordCount,
          }
        }
      );

      if (evaluationError) throw evaluationError;
      if (!evaluationData) throw new Error("No evaluation data received");

      const { error: updateError } = await supabase
        .from('profiles')
        .update({ remaining_attempts: profile.remaining_attempts - 1 })
        .eq('id', user?.id);

      if (updateError) throw updateError;

      toast.success("Essay evaluated successfully!");
      setTimeout(() => refetchEvaluation(), 1000);
    } catch (error) {
      console.error("Submission error:", error);
      toast.error("Failed to submit response");
    } finally {
      setIsSubmitting(false);
      toast.dismiss(loadingToast);
    }
  };

  const handleSaveDraft = async () => {
    if (!topicId) {
      toast.error("Please select a topic to save this draft.");
      highlightTopicSelector();
      return;
    }

    if (!draftId) {
      const { data, error } = await supabase
        .from("drafts")
        .insert({
          user_id: user?.id,
          topic_id: topicId,
          content,
          word_count: wordCount,
        })
        .select()
        .single();

      if (error) throw error;
      setDraftId(data.id);
    }

    const { error } = await supabase
      .from("drafts")
      .update({
        content,
        word_count: wordCount,
      })
      .eq("id", draftId);

    if (error) throw error;

    toast.success("Draft saved successfully!");
  };

  const handleShare = async () => {
    if (!draftId) return;

    try {
      const { error } = await supabase
        .from('drafts')
        .update({ is_public: true })
        .eq('id', draftId);

      if (error) throw error;

      const shareUrl = `${window.location.origin}/shared/${draftId}`;
      setShareUrl(shareUrl);
      setIsShareDialogOpen(true);
    } catch (error) {
      console.error("Error sharing draft:", error);
      toast.error("Failed to share draft");
    }
  };

  const highlightTopicSelector = () => {
    const topicSelector = document.querySelector('[data-topic-selector="true"]');
    if (topicSelector) {
      topicSelector.classList.add('animate-shake');
      setTimeout(() => {
        topicSelector.classList.remove('animate-shake');
      }, 3000);
    }
  };

  if (isLoading) {
    return <WritingAreaSkeleton />;
  }

  return (
    <div className={`transition-opacity duration-200 writing-area ${isChangingTopic ? 'opacity-0' : 'opacity-100'}`}>
      <ShareDialog
        isOpen={isShareDialogOpen}
        onOpenChange={setIsShareDialogOpen}
        shareUrl={shareUrl}
      />
      <Card className="bg-stone-100 border border-stone-200 rounded-xl dark:bg-stone-900 dark:border-stone-700">
        <CardHeader className="px-4 py-0">
          {!evaluation && (
            <WritingControls
              showWordCount={showWordCount}
              setShowWordCount={setShowWordCount}
              showTimer={showTimer}
              setShowTimer={setShowTimer}
              wordCount={wordCount}
            />
          )}
        </CardHeader>
        <CardContent className="px-0">
          {evaluation ? (
            <div className="bg-white rounded-xl px-8 py-6 border-b border-stone-200 dark:bg-stone-800 dark:border-stone-700 dark:text-white">
              <EvaluationResults evaluation={evaluation} essayContent={content} />
            </div>
          ) : (
            <WritingEditor isLocked={!!evaluation} content={content} topicId={topicId} onChange={setContent} />
          )}
        </CardContent>
        <CardFooter className="flex justify-between items-center px-8">
          {evaluation ? (
            <div className="w-full flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="order-2 sm:order-1">
                <p className="text-sm text-stone-400 italic dark:text-stone-500">
                  Keep writing - every word brings you closer to greatness!
                </p>
              </div>
              <div className="flex justify-end gap-2 order-1 sm:order-2 w-full sm:w-auto">
                <Button
                  variant="outline"
                  onClick={handleShare}
                  className="flex items-center gap-2 dark:bg-stone-800 dark:text-white dark:border-stone-700 dark:hover:bg-stone-700"
                >
                  <Share2 className="w-4 h-4" />
                  Share
                </Button>
                <Button
                  onClick={onStartOver}
                  className="bg-orange-500 text-white hover:bg-orange-500/90"
                >
                  Start Over
                </Button>
              </div>
            </div>
          ) : (
            <div className="ml-auto flex gap-4">
              <SaveDraftButton
                content={content}
                wordCount={wordCount}
                draftId={draftId}
                userId={user?.id}
                topicId={topicId}
                setDraftId={setDraftId}
              />
              <SubmitButton
                hasPaid={profile?.has_paid ?? false}
                isSubmitting={isSubmitting}
                onSubmit={handleSubmit}
                onSubscribe={handleSubscribe}
              />
            </div>
          )}
        </CardFooter>
      </Card>
    </div>
  );
};
